import React, { useState, useEffect, useCallback } from 'react';
import {
  sellToken,
  claimLevelIncome,
  getMyClaimRequests,
  getMySellWithdrawals,
} from '../../utils/api';
import Pagination from '../Pagination';

const SellClaim = ({ account, balance, price, onRefresh, f, fBC, vBC, toBC, bcRate, BC, SYM, readOnly = false }) => {
  const [sellAmount, setSellAmount] = useState('');
  const [isSelling, setIsSelling] = useState(false);
  const [sellResult, setSellResult] = useState(null);
  const [sellError, setSellError] = useState('');

  const [claimAmount, setClaimAmount] = useState('');
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [claimError, setClaimError] = useState('');
  const [claimRequests, setClaimRequests] = useState([]);

  // Sell requests (single-step: each sell IS a withdrawal request)
  const [sellRequests, setSellRequests] = useState([]);

  const PER_PAGE = 8;
  const [claimPage, setClaimPage] = useState(1);
  const [claimPages, setClaimPages] = useState(1);
  const [claimTotal, setClaimTotal] = useState(0);
  const [sellPage, setSellPage] = useState(1);
  const [sellPages, setSellPages] = useState(1);
  const [sellTotal, setSellTotal] = useState(0);

  const loadClaims = useCallback(async (pg = 1) => {
    if (!account) return;
    try {
      const res = await getMyClaimRequests(account, pg);
      setClaimRequests(res.requests || []);
      setClaimPages(res.pagination?.pages || 1);
      setClaimTotal(res.pagination?.total || 0);
    } catch {}
  }, [account]);

  const loadSellRequests = useCallback(async (pg = 1) => {
    if (!account) return;
    try {
      const res = await getMySellWithdrawals(account, pg);
      setSellRequests(res.requests || []);
      setSellPages(res.pagination?.pages || 1);
      setSellTotal(res.pagination?.total || 0);
    } catch {}
  }, [account]);

  useEffect(() => { loadClaims(claimPage); }, [loadClaims, claimPage]);
  useEffect(() => { loadSellRequests(sellPage); }, [loadSellRequests, sellPage]);

  // ─── Derived sell state (cumulative 2x-of-total-invested model) ───
  const maxBal = balance?.tokenBalance || 0;
  const tokenPr = price?.tokenPriceInUsdt || balance?.tokenPriceInUsdt || 0;
  const feePct = (price?.sellFeePercent || 10) / 100;
  const M = balance?.maxWithdrawMultiplier || price?.maxWithdrawMultiplier || 2;

  const totalInvested = balance?.totalInvested != null ? balance.totalInvested : (balance?.totalDeposit || 0);
  const cycleCap = balance?.maxWithdrawable != null ? balance.maxWithdrawable : (totalInvested * M);
  const totalWithdrawn = balance?.totalWithdrawn != null ? balance.totalWithdrawn : (balance?.totalSellUsdt || 0);
  const cycleRemaining = balance?.withdrawRemaining != null ? balance.withdrawRemaining : Math.max(0, cycleCap - totalWithdrawn);
  const portfolio = balance?.portfolioValueUsdt != null ? balance.portfolioValueUsdt : (maxBal * tokenPr);
  const sellExempt = balance?.sellExempt === true;

  const isWindowOpen = balance?.isWindowOpen != null ? balance.isWindowOpen : true;
  const windowDaysLeft = balance?.windowDaysLeft || 0;
  const windowOpensAt = balance?.withdrawWindowOpensAt;

  const gateRequired = balance?.gateRequired != null ? balance.gateRequired : true;
  const isGateMet = balance?.isGateMet != null ? balance.isGateMet : (!gateRequired || portfolio >= cycleCap);

  const hasPendingSell = sellRequests.some(w => w.status === 'pending');

  const sellAmt = parseFloat(sellAmount) || 0;
  const sellValue = sellAmt * tokenPr;
  const sellFee = sellValue * feePct;
  const sellReceive = sellValue - sellFee;          // 90% value = payout

  // Max sellable tokens whose GROSS value stays within the cycle remaining (cap
  // counts gross; the burn is deducted from it).
  const maxByCap = tokenPr > 0 ? (cycleRemaining / tokenPr) : 0;
  const maxSellable = Math.max(0, Math.min(maxBal, maxByCap));

  const isOverBalance = sellAmt > maxBal;
  const isOverCap = sellValue > cycleRemaining + 1e-4;
  const noCycle = cycleCap <= 0;

  // Whole sell action blocked (structural) → also disables the input field
  const sellBlocked =
    !!price?.isSellPaused || !isWindowOpen || noCycle || hasPendingSell ||
    (gateRequired && !isGateMet);

  const canSell =
    !sellBlocked && cycleRemaining > 0 && sellAmt > 0 && !isOverBalance && !isOverCap;

  // ─── Derived claim state ───
  const available = balance?.levelIncomeWallet || 0;
  // Treat floating-point dust as zero. After prior deductions levelIncomeWallet
  // can be a tiny residual (e.g. 2e-17) that is technically > 0 and displays as
  // 0.0000 — that would wrongly keep the field enabled. Anything below this
  // threshold is not meaningfully claimable.
  const CLAIM_DUST = 1e-6;
  const noClaimable = available <= CLAIM_DUST;
  const claimPaused = price?.isClaimPaused === true;
  const claimAmt = claimAmount ? (parseFloat(claimAmount) || 0) : 0;
  const usdtPerBC = (BC === 'USDT') ? 1 : (bcRate || 1);
  const enteredUsdt = claimAmt * usdtPerBC;
  const isOverClaim = enteredUsdt > available + 1e-9;
  // when field is empty we submit the full available balance
  const claimToSubmit = claimAmount ? enteredUsdt : available;
  const canClaim = !noClaimable && !claimPaused && claimToSubmit > 0 && claimToSubmit <= available + 1e-9;

  // block e / E / + / - in number fields (frontend input hardening)
  const blockInvalidKeys = (e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); };

  const handleSell = async () => {
    // ── frontend guards ──
    if (readOnly) { setSellError('Read-only admin view — actions are disabled.'); return; }
    if (sellBlocked) { setSellError('Selling is not available right now.'); return; }
    const amt = parseFloat(sellAmount);
    if (!amt || amt <= 0 || isNaN(amt)) { setSellError('Enter a valid amount'); return; }
    if (amt > maxBal) { setSellError(`Cannot sell more than balance (${f(maxBal, 2)})`); return; }
    if ((amt * tokenPr) > cycleRemaining + 1e-9) {
      setSellError(`Exceeds cap. Remaining: ${fBC(cycleRemaining, 4)} ${BC} (before burn)`); return;
    }

    setIsSelling(true); setSellError(''); setSellResult(null);
    try {
      const r = await sellToken({ walletAddress: account, tokenAmount: amt });
      setSellResult(r.request); setSellAmount(''); onRefresh(); setSellPage(1); loadSellRequests(1);
    } catch (e) { setSellError(e.response?.data?.error || 'Sell failed'); }
    setIsSelling(false);
  };

  const handleClaim = async () => {
    // ── frontend guards ──
    if (readOnly) { setClaimError('Read-only admin view — actions are disabled.'); return; }
    if (claimPaused) { setClaimError(`Claims are currently paused by the ${SYM} team.`); return; }
    if (noClaimable) { setClaimError('No level income available to claim'); return; }
    const amt = claimAmount ? enteredUsdt : available;
    if (!amt || amt <= 0 || isNaN(amt)) { setClaimError('Enter a valid amount'); return; }
    if (amt > available + 1e-9) { setClaimError(`Amount exceeds available (${fBC(available, 4)} ${BC})`); return; }

    setIsClaiming(true); setClaimError(''); setClaimResult(null);
    try {
      const r = await claimLevelIncome({ walletAddress: account, amount: amt });
      setClaimResult(r); setClaimAmount(''); onRefresh(); setClaimPage(1); loadClaims(1);
    } catch (e) { setClaimError(e.response?.data?.error || 'Claim failed'); }
    setIsClaiming(false);
  };

  const statusBadge = (s) => {
    const map = { pending: 'badge-pending', approved: 'badge-approved', rejected: 'badge-rejected' };
    return <span className={`d-status-badge ${map[s] || ''}`}>{s}</span>;
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';

  return (
    <div className="fade-in sc-root">
      <style>{`
        .sc-root { width: 100%; box-sizing: border-box; }
        .sc-root * { box-sizing: border-box; }
        .sc-root .d-trade-row { display: flex; flex-direction: column; gap: 16px; width: 100%; }
        .sc-root .d-trade.card { width: 100%; margin: 0; overflow: hidden; }
        .sc-root h3 { font-size: 18px; margin-bottom: 6px; }
        .sc-root h4 { font-size: 15px; margin: 14px 0 8px; }
        .sc-root .d-trade-sub { font-size: 13px; word-break: break-word; }
        .sc-root .d-claim-rows { margin: 10px 0; }
        .sc-root .d-claim-line { display: flex; justify-content: space-between; gap: 10px; padding: 6px 0; font-size: 13px; }
        .sc-root .d-claim-line .mono { text-align: right; word-break: break-all; }
        /* keep the highlighted row inside the card padding (no edge-to-edge bleed) */
        .sc-root .d-claim-highlight { margin: 4px 0; padding: 9px 12px; border-radius: 10px; }
        .sc-root .d-form { width: 100%; }
        .sc-root .d-field, .sc-root .d-field-with-max { width: 100%; margin-bottom: 12px; }
        .sc-root .d-field label, .sc-root .d-field-with-max label { display: block; font-size: 13px; margin-bottom: 6px; }
        .sc-root .input { width: 100%; font-size: 16px; padding: 12px 14px; border-radius: 10px; }
        .sc-root .input:disabled { opacity: .5; cursor: not-allowed; }
        .sc-root .d-field-with-max { position: relative; }
        .sc-root .d-field-with-max .input { padding-right: 68px; }
        .sc-root .d-max-btn {
          position: absolute; right: 8px; bottom: 8px; height: 34px; padding: 0 12px;
          border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer;
        }
        .sc-root .d-max-btn:disabled { opacity: .5; cursor: not-allowed; }
        .sc-root .d-calc { margin: 10px 0; padding: 10px 12px; border-radius: 10px; }
        .sc-root .d-calc-row { display: flex; justify-content: space-between; gap: 10px; padding: 4px 0; font-size: 13px; }
        .sc-root .btn-lg { width: 100%; padding: 14px; font-size: 15px; border-radius: 12px; margin-top: 4px; }
        .sc-root .btn-lg:disabled { opacity: .55; cursor: not-allowed; }
        .sc-root .d-claim-note { font-size: 12px; line-height: 1.5; margin-top: 10px; }
        .sc-root .notice { font-size: 13px; padding: 10px 12px; border-radius: 10px; margin: 8px 0; }
        .sc-root .table-wrap { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .sc-root .table { width: 100%; border-collapse: collapse; font-size: 12.5px; white-space: nowrap; }
        .sc-root .table th, .sc-root .table td { padding: 8px 10px; text-align: left; }
        .sc-root .sc-refunded {
          display: inline-block; margin-left: 6px; font-size: 11px; font-weight: 700;
          color: #16a34a; vertical-align: middle;
        }
        @media (min-width: 820px) {
          .sc-root .d-trade-row { flex-direction: row; align-items: flex-start; }
          .sc-root .d-trade.card { flex: 1 1 0; min-width: 0; }
        }
      `}</style>

      <div className="d-trade-row">
        {/* Sell */}
        <div className="d-trade card">
          <h3>Sell {SYM} Tokens</h3>
          <p className="d-trade-sub">Balance: <strong className="mono">{f(maxBal, 2)} {SYM}</strong> &nbsp;·&nbsp; Portfolio: <strong className="mono">{vBC(portfolio, 4)}</strong></p>

          {price?.isSellPaused && <div className="notice notice-warning">Selling is currently paused.</div>}

          {/* Cap summary (or exempt notice) */}
          {sellExempt ? (
            <div className="d-claim-rows">
              <div className="notice notice-success">⭐ Exempt wallet — no cap, no lock. Withdraw any amount, anytime (up to your balance & liquidity).</div>
              <div className="d-claim-line"><span>Total Invested</span><strong className="mono">{vBC(totalInvested, 4)}</strong></div>
              <div className="d-claim-line d-claim-highlight"><span>Portfolio Value</span><strong className="mono text-accent">{vBC(portfolio, 4)}</strong></div>
            </div>
          ) : (
            <div className="d-claim-rows">
              <div className="d-claim-line"><span>Total Invested</span><strong className="mono">{vBC(totalInvested, 4)}</strong></div>
              <div className="d-claim-line"><span>Cap ({M}× total invested)</span><strong className="mono">{vBC(cycleCap, 4)}</strong></div>
              <div className="d-claim-line"><span>Total Withdrawn</span><strong className="mono">{vBC(totalWithdrawn, 4)}</strong></div>
              <div className="d-claim-line d-claim-highlight"><span>Remaining</span><strong className="mono text-accent">{vBC(cycleRemaining, 4)}</strong></div>
            </div>
          )}

          {/* Status gates */}
          {noCycle && <div className="notice notice-warning">No active investment. Buy {SYM} first to start a withdrawal cycle.</div>}
          {!noCycle && !isWindowOpen && (
            <div className="notice notice-warning">
              Withdrawal window opens in <strong>{windowDaysLeft} day(s)</strong>{windowOpensAt ? ` — on ${fmtDate(windowOpensAt)}` : ''}. You can sell after that.
            </div>
          )}
          {!noCycle && isWindowOpen && gateRequired && !isGateMet && (
            <div className="notice notice-warning">
              Selling unlocks once your portfolio reaches <strong>{vBC(cycleCap, 4)}</strong> ({M}× total invested). Current: {vBC(portfolio, 4)}.
            </div>
          )}
          {hasPendingSell && <div className="notice notice-warning">You have a pending sell request. Wait for {SYM} team approval before selling again.</div>}

          <div className="d-form">
            <div className="d-field d-field-with-max"><label>{SYM} to Sell</label>
              <input type="number" min="0" inputMode="decimal" placeholder="0.00" value={sellAmount}
                disabled={sellBlocked}
                onKeyDown={blockInvalidKeys}
                onChange={(e) => { setSellAmount(e.target.value); setSellError(''); setSellResult(null); }} className="input" />
              <button className="d-max-btn" disabled={sellBlocked}
                onClick={() => setSellAmount(maxSellable > 0 ? String(Math.floor(maxSellable * 1e6) / 1e6) : '0')}>MAX</button>
            </div>
            {sellAmt > 0 && tokenPr > 0 && (
              <div className="d-calc">
                <div className="d-calc-row"><span>Value</span><strong>{vBC(sellValue, 4)}</strong></div>
                <div className="d-calc-row"><span>Burn ({(feePct * 100).toFixed(0)}%)</span><strong className="text-red">-{vBC(sellFee, 4)}</strong></div>
                <div className="d-calc-row d-calc-highlight"><span>You receive (on approval)</span><strong className="text-green">{vBC(sellReceive, 4)}</strong></div>
              </div>
            )}
            {isOverBalance && <div className="notice notice-error">Cannot sell more than balance ({f(maxBal, 2)})</div>}
            {!isOverBalance && isOverCap && <div className="notice notice-error">Exceeds cycle cap. Remaining: {vBC(cycleRemaining, 4)}</div>}
            {sellError && <div className="notice notice-error">{sellError}</div>}
            {sellResult && <div className="notice notice-success">Sell request submitted: <strong>{vBC(sellResult.payoutUsdt, 4)}</strong>. Awaiting {SYM} team approval — {BC} auto-credited once approved.</div>}
            <button className="btn btn-danger btn-lg" onClick={handleSell} disabled={readOnly || isSelling || !canSell}>
              {readOnly ? 'Read-only view' : isSelling ? 'Processing...' : `Sell ${SYM}`}
            </button>
            <p className="d-claim-note">Selling submits a withdrawal request to the {SYM} team. On approval, {(feePct * 100).toFixed(0)}% is burned and the {BC} value is auto-credited to your wallet. Rejected requests return your tokens.</p>
          </div>

          {/* Sell request history */}
          {sellRequests.length > 0 && (
            <div className="d-claim-history"><h4>Sell Requests</h4>
              <div className="table-wrap"><table className="table">
                <thead><tr><th>Date</th><th>{SYM} Sold</th><th>Payout</th><th>Status</th></tr></thead>
                <tbody>{sellRequests.map(w => (
                  <tr key={w._id}>
                    <td>{fmtDate(w.requestedAt)}</td>
                    <td className="mono">{f(w.tokenAmount, 2)}</td>
                    <td className="mono">{fBC(w.amount, 4)} {w.currency || BC}</td>
                    <td>
                      {statusBadge(w.status)}
                      {w.status === 'rejected' && <span className="sc-refunded">Refunded</span>}
                    </td>
                  </tr>
                ))}</tbody>
              </table></div>
              <Pagination page={sellPage} pages={sellPages} total={sellTotal} perPage={PER_PAGE} onPage={setSellPage} />
            </div>
          )}
        </div>

        {/* Claim */}
        <div className="d-trade card">
          <h3>Claim Level Income</h3>
          <div className="d-claim-rows">
            <div className="d-claim-line"><span>Total Earned</span><strong className="mono">{vBC(balance?.levelIncomeEarned, 4)}</strong></div>
            <div className="d-claim-line"><span>Claimed</span><strong className="mono">{vBC(balance?.levelIncomeClaimed, 4)}</strong></div>
            <div className="d-claim-line d-claim-highlight"><span>Available</span><strong className="mono text-accent">{vBC(available, 4)}</strong></div>
          </div>
          <div className="d-form">
            <div className="d-field"><label>Claim Amount ({BC})</label>
              <input type="number" min="0" inputMode="decimal" placeholder={fBC(available, 4)} value={claimAmount}
                disabled={noClaimable || claimPaused}
                onKeyDown={blockInvalidKeys}
                onChange={(e) => { setClaimAmount(e.target.value); setClaimError(''); setClaimResult(null); }} className="input" />
              <p className="d-claim-note" style={{ marginTop: 6 }}>
                {claimAmt > 0 && BC !== 'USDT' ? <>&#8776; {f(enteredUsdt, 4)} USDT &#183; </> : null}
                Max: {fBC(available, 4)} {BC}
                {' '}<button type="button" onClick={() => setClaimAmount(String(toBC(available)))} disabled={noClaimable || claimPaused} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, font: 'inherit' }}>Use max</button>
              </p>
            </div>
            {price?.isClaimPaused && <div className="notice notice-warning">Claims are currently paused by the {SYM} team.</div>}
            {!claimPaused && noClaimable && <div className="notice notice-warning">No level income available to claim yet.</div>}
            {!noClaimable && isOverClaim && <div className="notice notice-error">Amount exceeds available ({vBC(available, 4)})</div>}
            {claimError && <div className="notice notice-error">{claimError}</div>}
            {claimResult && <div className="notice notice-success">{claimResult.message}</div>}
            <button className="btn btn-primary btn-lg" onClick={handleClaim} disabled={readOnly || isClaiming || !canClaim}>
              {readOnly ? 'Read-only view' : isClaiming ? 'Submitting...' : 'Submit Claim Request'}
            </button>
            <p className="d-claim-note">Your claim will be reviewed by the {SYM} team before {BC} is released.</p>
          </div>
          {claimRequests.length > 0 && (
            <div className="d-claim-history"><h4>Claim Requests</h4>
              <div className="table-wrap"><table className="table">
                <thead><tr><th>Date</th><th>Amount</th><th>Status</th></tr></thead>
                <tbody>{claimRequests.map(cr => (
                  <tr key={cr._id}>
                    <td>{fmtDate(cr.requestedAt)}</td>
                    <td className="mono">{vBC(cr.amount, 4)}</td>
                    <td>
                      {statusBadge(cr.status)}
                      {cr.status === 'rejected' && <span className="sc-refunded">Refunded</span>}
                    </td>
                  </tr>
                ))}</tbody>
              </table></div>
              <Pagination page={claimPage} pages={claimPages} total={claimTotal} perPage={PER_PAGE} onPage={setClaimPage} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellClaim;